import React, { useState } from "react";
import {
  getActiveInnings,
  getBattingRows,
  getBowlingRows,
} from "../../utils/publicScorecardAdapter";
import { formatPlayerName } from "../../utils/nameFormatter";

export interface PublicMatch {
  id: string;
  teamA: string;
  teamB: string;
  status: "live" | "upcoming" | "completed";
  scoreA?: string;
  scoreB?: string;
  overs?: string;
  format?: string;
  result?: string;
  startTime?: string;
  scheduledAt?: string | null;
  venue?: string;
  lastUpdated?: string;
  matchData?: any;
  matchType?: string;
}

interface MatchCardProps {
  match: PublicMatch;
  onClick: () => void;
  compact?: boolean;
}

export function MatchCard({ match, onClick, compact = false }: MatchCardProps) {
  const [expanded, setExpanded] = useState(false);

  const activeInnings = match.matchData ? getActiveInnings(match.matchData) : [];
  const hasScorecard = activeInnings.length > 0 && (match.status === "live" || match.status === "completed");

  const statusBadge =
    match.status === "live"
      ? { label: "LIVE", cls: "bg-red-500" }
      : match.status === "completed"
      ? { label: "RESULT", cls: "bg-slate-600" }
      : { label: "UPCOMING", cls: "bg-emerald-600" };

  if (compact) {
    return (
      <div
        onClick={onClick}
        className="glass-card p-3 cursor-pointer hover:border-slate-400/30 transition-all"
      >
        <div className="flex items-center justify-between mb-2">
          <span className={`${statusBadge.cls} text-white text-xs font-bold px-2 py-0.5 rounded`}>
            {statusBadge.label}
          </span>
          <span className="text-slate-400/70 text-xs">{match.format}</span>
        </div>
        <div className="space-y-1">
          <div className="flex items-center justify-between">
            <span className="text-white font-semibold text-sm">{match.teamA}</span>
            {match.scoreA && <span className="text-emerald-400 font-bold text-sm">{match.scoreA}</span>}
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white font-semibold text-sm">{match.teamB}</span>
            {match.scoreB && <span className="text-white/80 font-bold text-sm">{match.scoreB}</span>}
          </div>
        </div>
        {match.result && (
          <div className="mt-2 text-emerald-400 text-xs font-medium truncate">{match.result}</div>
        )}
        {match.overs && (
          <div className="mt-1 text-slate-400/60 text-xs">Overs: {match.overs}</div>
        )}
        {!match.scoreA && match.startTime && (
          <div className="mt-1 text-slate-400/60 text-xs">{match.startTime}</div>
        )}
      </div>
    );
  }

  return (
    <div className="glass-card overflow-hidden">
      <div
        onClick={(e) => {
          if ((e.target as HTMLElement).closest(".expand-btn")) return;
          onClick();
        }}
        className="p-4 cursor-pointer hover:bg-slate-800/30 transition-all"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className={`${statusBadge.cls} text-white text-xs font-bold px-2.5 py-1 rounded shadow`}>
              {statusBadge.label}
            </span>
            {match.status === "live" && (
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            )}
            <span className="text-slate-400/70 text-sm">{match.format}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-slate-400/50 text-xs">{match.lastUpdated}</span>
            {hasScorecard && (
              <button
                className="expand-btn text-slate-400 hover:text-white p-1 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
              >
                <svg
                  className={`w-5 h-5 transition-transform ${expanded ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-white font-bold text-lg">{match.teamA}</span>
            <span className="text-emerald-400 font-black text-xl">{match.scoreA || "-"}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-white font-bold text-lg">{match.teamB}</span>
            <span className="text-white/90 font-black text-xl">{match.scoreB || "Yet to bat"}</span>
          </div>
        </div>

        {match.overs && (
          <div className="mt-3 text-slate-300/60 text-sm">Overs: {match.overs}</div>
        )}
        {match.result && (
          <div className="mt-3 text-emerald-400 text-sm font-semibold">{match.result}</div>
        )}
        {!match.scoreA && match.startTime && (
          <div className="mt-3 text-slate-400/60 text-sm">{match.startTime}</div>
        )}
      </div>

      {expanded && hasScorecard && (
        <div className="border-t border-slate-700 bg-slate-900/30 p-4 space-y-4">
          {activeInnings.map((innings: any, idx: number) => {
            const battingTeamName = innings.battingTeam === "A" ? match.teamA : match.teamB;
            const battingTeamPlayers =
              innings.battingTeam === "A"
                ? match.matchData.teamAPlayers
                : match.matchData.teamBPlayers;
            const bowlingTeamPlayers =
              innings.battingTeam === "A"
                ? match.matchData.teamBPlayers
                : match.matchData.teamAPlayers;

            const battingRows = getBattingRows(innings, battingTeamPlayers, match.format);
            const bowlingRows = getBowlingRows(innings, bowlingTeamPlayers);

            return (
              <div key={idx} className="space-y-3">
                <h4 className="text-white font-bold text-sm">{battingTeamName} Innings</h4>

                {battingRows.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="text-left p-2">Batter</th>
                          <th className="text-right p-2">R</th>
                          <th className="text-right p-2">B</th>
                          <th className="text-right p-2">4s</th>
                          <th className="text-right p-2">6s</th>
                          <th className="text-right p-2">SR</th>
                        </tr>
                      </thead>
                      <tbody>
                        {battingRows.slice(0, 5).map((b, i) => (
                          <tr key={i} className="border-t border-slate-700">
                            <td className="p-2 text-white">{formatPlayerName(b.name)}</td>
                            <td className="p-2 text-right text-slate-300">{b.runs}</td>
                            <td className="p-2 text-right text-slate-400">{b.balls}</td>
                            <td className="p-2 text-right text-slate-400">{b.fours}</td>
                            <td className="p-2 text-right text-slate-400">{b.sixes}</td>
                            <td className="p-2 text-right text-slate-400">{b.strikeRate}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}

                {bowlingRows.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-800">
                        <tr>
                          <th className="text-left p-2">Bowler</th>
                          <th className="text-right p-2">O</th>
                          <th className="text-right p-2">R</th>
                          <th className="text-right p-2">W</th>
                          <th className="text-right p-2">Econ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bowlingRows.slice(0, 5).map((b, i) => (
                          <tr key={i} className="border-t border-slate-700">
                            <td className="p-2 text-white">{formatPlayerName(b.name)}</td>
                            <td className="p-2 text-right text-slate-300">{b.overs}</td>
                            <td className="p-2 text-right text-slate-400">{b.runs}</td>
                            <td className="p-2 text-right text-emerald-400 font-bold">{b.wickets}</td>
                            <td className="p-2 text-right text-slate-400">{b.economy}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          <button
            onClick={onClick}
            className="w-full text-center text-emerald-400 hover:text-emerald-300 text-sm font-semibold py-2 transition-colors"
          >
            View Full Scorecard →
          </button>
        </div>
      )}
    </div>
  );
}

export function MatchCardSkeleton({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <div className="glass-card p-3 animate-pulse">
        <div className="flex items-center justify-between mb-2">
          <div className="h-4 w-16 bg-emerald-700/30 rounded" />
          <div className="h-3 w-12 bg-emerald-700/30 rounded" />
        </div>
        <div className="space-y-2">
          <div className="flex justify-between">
            <div className="h-4 w-28 bg-emerald-700/30 rounded" />
            <div className="h-4 w-12 bg-emerald-700/30 rounded" />
          </div>
          <div className="flex justify-between">
            <div className="h-4 w-24 bg-emerald-700/30 rounded" />
            <div className="h-4 w-14 bg-emerald-700/30 rounded" />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="glass-card p-4 animate-pulse">
      <div className="flex justify-between mb-4">
        <div className="h-5 w-16 bg-emerald-700/30 rounded" />
        <div className="h-4 w-12 bg-emerald-700/30 rounded" />
      </div>
      <div className="space-y-3">
        <div className="flex justify-between">
          <div className="h-5 w-32 bg-emerald-700/30 rounded" />
          <div className="h-6 w-16 bg-emerald-700/30 rounded" />
        </div>
        <div className="flex justify-between">
          <div className="h-5 w-28 bg-emerald-700/30 rounded" />
          <div className="h-6 w-20 bg-emerald-700/30 rounded" />
        </div>
      </div>
    </div>
  );
}
